import { config } from '../config';
import type {
  ConversationInboxItem,
  ConversationMessage,
  ConversationClosedReason,
  InboxFilters,
  ThreadControlTake,
} from '../types/dashboard';
import type { PageTokenState } from '../lib/pageToken';
import type { Assignee } from '../lib/assignees';

/**
 * Service layer for the unified operator inbox. Wraps the gateway's
 * `/api/operator/conversations` endpoint family.
 *
 * **Design choice:** pure functions, each takes `apiFetch` (the Bearer +
 * 401-retry wrapper from `AuthContext`) and parameters. Keeps the service
 * decoupled from React lifecycle — call sites are hooks/contexts that
 * already have `useAuth().apiFetch`.
 *
 * Endpoints (server-side, see
 *   messaging-gateway/src/modules/conversation/conversation-inbox.controller.ts):
 *
 *   GET    /api/operator/conversations
 *            (tenant-scoped when pageId omitted; single-page when provided)
 *   GET    /api/operator/conversations/:id/messages
 *   POST   /api/operator/conversations/:id/takeover
 *   POST   /api/operator/conversations/:id/handback
 *   GET    /api/operator/conversations/:id/assignees        (admin)
 *   POST   /api/operator/conversations/:id/assign { operatorId, expectedOperator } (admin)
 *   POST   /api/operator/conversations/:id/close   { reason? }
 *   POST   /api/operator/conversations/:id/reopen
 *   PATCH  /api/operator/conversations/:id/read
 */

type ApiFetch = (url: string, init?: RequestInit) => Promise<Response>;

/** Backend response shape (envelope around the list). */
/**
 * One entry in a conversation's history.
 *
 * `by` is an operatorId or `'system'`; `byName` is the display name snapshotted
 * when the event was written, so a later rename does not rewrite history. Null
 * for system events and for rows predating the field — fall back to `by`.
 */
export interface ConversationEvent {
  type:
    | 'opened'
    | 'claimed'
    | 'released'
    | 'transferred'
    | 'handed_back'
    | 'closed'
    | 'reopened'
    | 'sender_blocked'
    | 'sender_unblocked'
    | 'handler_changed'
    | 'ai_skipped_standby';
  by: string;
  byName: string | null;
  from: string | null;
  to: string | null;
  /** Name of `to` when someone else chose them (an admin's assign); null on other events and older rows. */
  toName?: string | null;
  reason: string | null;
  at: string;
}

interface ListResponse {
  list: ConversationInboxItem[];
  /** Set when a name-search returned more matching senders than the cap (500). */
  searchTruncated?: boolean;
}

/** Service return shape — bundles the list with the truncation signal. */
export interface InboxListResult {
  list: ConversationInboxItem[];
  searchTruncated: boolean;
}

interface MessagesResponse {
  messages: ConversationMessage[];
  count: number;
  metadata?: { conversationId: string };
}

/**
 * Result of a manual sender-profile refresh.
 *
 * A discriminated union rather than `{ok, senderName?}` so a caller cannot read
 * a name off a failed refresh — on failure there is no name, and TypeScript
 * should say so rather than hand back `undefined` for the UI to render blank.
 *
 * - `cooldown` — another refresh for this sender ran within the last 30s.
 * - `unavailable` — Graph declined or could not be reached. The underlying
 *   error stays server-side; it names internal ids and means nothing here.
 */
export type RefreshProfileResult =
  | { ok: true; senderName: string; senderAvatar?: string }
  | { ok: false; reason: 'cooldown' | 'unavailable' };

/**
 * A conversation action the gateway refused — almost always because somebody
 * else acted first.
 *
 * These used to surface as `new Error("release failed: 409 {…}")`, which the
 * context logged to the console and swallowed. The operator saw the row leave
 * their list and silently come back, with no explanation — indistinguishable
 * from a UI glitch. The gateway has always returned a machine-readable `code`
 * and the current holder; nothing read them.
 *
 * `code` values: `ASSIGNED_TO_OTHER` (close/handback on someone else's row),
 * `NOT_ASSIGNED_TO_YOU` (release when you are not the holder),
 * `ALREADY_ASSIGNED` (takeover lost the race).
 */
export class InboxActionError extends Error {
  constructor(
    readonly action: string,
    readonly status: number,
    readonly code?: string,
    readonly assignedOperator?: string | null,
    message?: string,
    /** The holder's name, snapshotted when they took it (gateway 409s since the holder-name change). */
    readonly assignedOperatorName?: string | null,
    /** TARGET_UNAVAILABLE: why the person cannot take it now (break | off | offline). */
    readonly reason?: string,
  ) {
    super(message || `${action} failed: ${status}`);
    this.name = 'InboxActionError';
  }

  /** True when another operator's action caused this — worth telling the user. */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

/**
 * Parse a failed action response and throw. Always throws — the `Promise<never>`
 * return lets callers write `if (!res.ok) await throwActionError(res, 'x')`
 * without TypeScript losing track of control flow.
 */
async function throwActionError(res: Response, action: string): Promise<never> {
  let code: string | undefined;
  let assignedOperator: string | null | undefined;
  let assignedOperatorName: string | null | undefined;
  let message: string | undefined;
  let reason: string | undefined;
  try {
    // Nest wraps the thrown object under `message` for ConflictException.
    const body = await res.json();
    const payload = body?.message && typeof body.message === 'object' ? body.message : body;
    code = payload?.code;
    assignedOperator = payload?.assignedOperator ?? null;
    assignedOperatorName = typeof payload?.assignedOperatorName === 'string' ? payload.assignedOperatorName : null;
    message = typeof payload?.message === 'string' ? payload.message : undefined;
    reason = typeof payload?.reason === 'string' ? payload.reason : undefined;
  } catch {
    // Non-JSON body (proxy error page, gateway down). Fall through to defaults.
  }
  throw new InboxActionError(action, res.status, code, assignedOperator, message, assignedOperatorName, reason);
}

/**
 * Translate `InboxFilters.view` (UX segment) into backend query params.
 *
 * Every segment now maps to a real server-side filter. It did not use to:
 * `mine` was sent as `handler=operator` and narrowed in the browser, and
 * `unassigned` as `handler=ai`. Both were wrong in ways that mattered —
 * see `narrowClientSide` below and ROADMAP → Assignment model in the gateway.
 *
 * Tabs are **workload states** (whose work is this, is it done). "Who is
 * handling it" stays a separate `handler` filter: "the bot has it" is nobody's
 * work, so it is not a tab.
 */
function viewToParams(filters: InboxFilters): URLSearchParams {
  const params = new URLSearchParams();
  switch (filters.view) {
    case 'open':
      params.set('status', 'OPEN');
      break;
    case 'closed':
      params.set('status', 'CLOSED');
      break;
    case 'mine':
      params.set('status', 'OPEN');
      // Resolved server-side against assignedOperator. The backend always uses
      // the authenticated caller, so no operatorId is sent — passing one would
      // let an operator enumerate a colleague's queue.
      params.set('mine', 'true');
      break;
    case 'unassigned':
      params.set('status', 'OPEN');
      // "A human is needed and nobody holds it" — handler=operator with no
      // assignedOperator. This used to send handler=ai, which means "the bot
      // has it": nobody's work, and the opposite of what the tab promises.
      // A released conversation is exactly that shape and used to land in no
      // tab at all.
      params.set('unassigned', 'true');
      break;
    case 'all':
      // No status filter — fetch both OPEN and CLOSED.
      break;
    case 'blocked':
      // Conversations whose SENDER is blocked. Every other view excludes these
      // server-side, so this is the only way to reach them — and the only place
      // Unblock can be offered. No status filter: a blocked sender's threads
      // are usually CLOSED, but one can still be OPEN if they were blocked
      // from the detail panel rather than via a close.
      params.set('blocked', 'true');
      break;
  }

  if (filters.pageId) params.set('pageId', filters.pageId);
  // Channel filter: backend accepts a single channel today. Multi-channel
  // narrowing is done front-side when filters.channels.length > 1.
  if (filters.channels?.length === 1) params.set('channel', filters.channels[0]);
  if (filters.handler && filters.view !== 'mine' && filters.view !== 'unassigned') {
    params.set('handler', filters.handler);
  }
  if (filters.tag) params.set('tag', filters.tag);
  if (filters.senderTag) params.set('senderTag', filters.senderTag);
  if (filters.unreadOnly) params.set('operatorUnread', 'true');
  // Sender-name search — backend handles prefix matching and PSID resolution.
  // Forward only when non-empty; empty string = no search active.
  if (filters.search && filters.search.trim()) params.set('search', filters.search.trim());

  return params;
}

/**
 * Client-side narrowing pass — for filters the backend genuinely cannot
 * express. Today that is only multi-channel selection.
 *
 * The `mine` narrowing that used to live here has been removed, and it is
 * worth knowing why so it is not reintroduced: it ran **after** server-side
 * pagination. The server returned page 1 of 50 operator-held conversations,
 * then this pass discarded everyone else's — so "Mine" showed whichever of
 * yours happened to fall in that page, and the count was wrong. Any filter
 * that removes rows must be applied before `limit`/`offset`, which means on
 * the server.
 */
function narrowClientSide(
  list: ConversationInboxItem[],
  filters: InboxFilters,
): ConversationInboxItem[] {
  let out = list;
  if (filters.channels && filters.channels.length > 1) {
    const allowed = new Set(filters.channels);
    out = out.filter((c) => allowed.has(c.channel));
  }
  return out;
}

export const inboxService = {
  /**
   * List inbox rows. Returns the full embedded shape (sender profile +
   * lastMessage preview already on each row — no follow-up fetches needed).
   *
   * `limit` / `offset` for pagination. Backend caps at 200/req.
   */
  async list(
    apiFetch: ApiFetch,
    filters: InboxFilters,
    options: { tenantId?: string; limit?: number; offset?: number } = {},
  ): Promise<InboxListResult> {
    const params = viewToParams(filters);
    // Always forward the active tenantId when known. Without it the gateway
    // falls back to "every page across every tenant the operator belongs to"
    // and leaks cross-tenant rows on /:slug/inbox. The senderTag filter
    // additionally requires it on the backend (sender_tags lookup is
    // tenant-scoped).
    if (options.tenantId) params.set('tenantId', options.tenantId);
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    if (options.offset !== undefined) params.set('offset', String(options.offset));
    const qs = params.toString();
    const url = `${config.apiServer}/api/operator/conversations${qs ? `?${qs}` : ''}`;
    const res = await apiFetch(url, { method: 'GET' });
    if (!res.ok) throw new Error(`inbox list failed: ${res.status} ${await res.text()}`);
    const body = (await res.json()) as ListResponse;
    return {
      list: narrowClientSide(body.list ?? [], filters),
      searchTruncated: body.searchTruncated === true,
    };
  },

  /**
   * Pages whose Meta connection is currently expired, across the operator's pages (token spec B3-4). Live
   * changes arrive as `page:token:changed`.
   */
  async listPageTokenStates(apiFetch: ApiFetch): Promise<PageTokenState[]> {
    const res = await apiFetch(`${config.apiServer}/api/operator/pages/token-states`, { method: 'GET' });
    if (!res.ok) throw new Error(`page token states failed: ${res.status}`);
    const body = (await res.json()) as { pages?: PageTokenState[] };
    return body.pages ?? [];
  },

  /**
   * Fetch a single conversation row by id. Used to walk
   * `previousConversationId` chains — each hop is a single-row lookup.
   * Returns null when the conversation doesn't exist OR the operator
   * lacks access (backend returns 400 for both to avoid leaking).
   */
  async getOne(apiFetch: ApiFetch, id: string): Promise<ConversationInboxItem | null> {
    const url = `${config.apiServer}/api/operator/conversations/${id}`;
    const res = await apiFetch(url, { method: 'GET' });
    if (res.status === 400 || res.status === 404) return null;
    if (!res.ok) throw new Error(`inbox getOne failed: ${res.status}`);
    return (await res.json()) as ConversationInboxItem;
  },

  /**
   * Conversation history — who handled this thread and when.
   *
   * Oldest first, as the gateway returns it. An empty array means "no history
   * recorded", never "nothing happened": the journal started partway through
   * this system's life and there was no backfill, so older conversations
   * legitimately have none.
   */
  async listEvents(apiFetch: ApiFetch, id: string): Promise<ConversationEvent[]> {
    const url = `${config.apiServer}/api/operator/conversations/${id}/events`;
    const res = await apiFetch(url, { method: 'GET' });
    // 400/404 is the gateway's deliberate "no access OR not found" — it refuses
    // to distinguish them so an operator cannot probe for conversations on
    // pages they don't hold. Nothing to show either way.
    if (res.status === 400 || res.status === 404) return [];
    if (!res.ok) throw new Error(`inbox listEvents failed: ${res.status}`);
    const body = (await res.json()) as { events?: ConversationEvent[] };
    return body.events ?? [];
  },

  /**
   * Fetch messages for a conversation. Backend currently requires `pageId`
   * + `psid` query params alongside the id — see controller signature. We
   * pull them from the row the caller already has in state.
   */
  async getMessages(
    apiFetch: ApiFetch,
    params: {
      id: string;
      pageId: string;
      psid: string;
      channel: string;
      limit?: number;
      before?: number;
      order?: 'asc' | 'desc';
    },
  ): Promise<ConversationMessage[]> {
    const qs = new URLSearchParams({
      pageId: params.pageId,
      psid: params.psid,
      channel: params.channel,
      ...(params.limit ? { limit: String(params.limit) } : {}),
      ...(params.before ? { before: String(params.before) } : {}),
      ...(params.order ? { order: params.order } : {}),
    });
    const url = `${config.apiServer}/api/operator/conversations/${params.id}/messages?${qs.toString()}`;
    const res = await apiFetch(url, { method: 'GET' });
    if (!res.ok) throw new Error(`inbox getMessages failed: ${res.status}`);
    const body = (await res.json()) as MessagesResponse;
    return body.messages ?? [];
  },

  /**
   * `botSilenced` is the gateway's report of whether `chatbot_mn` acknowledged
   * that a human now holds this thread. `false` means the claim succeeded but
   * **the bot may still be replying** — the operator does not exclusively hold
   * the conversation. Returned rather than discarded so the UI can say so; a
   * claim that silently half-worked is the failure mode operators cannot
   * diagnose on their own.
   */
  async takeover(
    apiFetch: ApiFetch,
    id: string,
  ): Promise<{ botSilenced?: boolean; threadControl?: ThreadControlTake }> {
    const res = await apiFetch(`${config.apiServer}/api/operator/conversations/${id}/takeover`, {
      method: 'POST',
    });
    if (!res.ok) await throwActionError(res, 'takeover');
    // Older gateways return `{ok:true}` without `botSilenced` / `threadControl`. Absent =
    // unknown/not attempted, never failure, so this is safe under version skew.
    return (await res.json().catch(() => ({}))) as {
      botSilenced?: boolean;
      threadControl?: ThreadControlTake;
    };
  },

  async handback(apiFetch: ApiFetch, id: string): Promise<void> {
    const res = await apiFetch(`${config.apiServer}/api/operator/conversations/${id}/handback`, {
      method: 'POST',
    });
    if (!res.ok) await throwActionError(res, 'handback');
  },

  /**
   * Return the conversation to the unassigned queue — it stays human work,
   * just nobody's.
   *
   * Distinct from `handback`, and the distinction is the whole point:
   * handback gives the customer back to the **bot**, release offers the
   * conversation to **another operator**. Ending a shift mid-case used to
   * mean handback, which silently re-enables the bot in the middle of a human
   * conversation.
   *
   * Only the assigned operator — or an admin (`reassign`) — may release; the
   * gateway answers 409 NOT_ASSIGNED_TO_YOU otherwise.
   */
  async release(apiFetch: ApiFetch, id: string, expectedOperator?: string): Promise<void> {
    // An admin releasing someone else's names the holder they saw, so a newer holder is never released.
    const res = await apiFetch(`${config.apiServer}/api/operator/conversations/${id}/release`, {
      method: 'POST',
      ...(expectedOperator
        ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expectedOperator }) }
        : {}),
    });
    if (!res.ok) await throwActionError(res, 'release');
  },

  /** Admin only (`reassign`): the workspace members this conversation can be given to. */
  async assignees(apiFetch: ApiFetch, id: string): Promise<Assignee[]> {
    const res = await apiFetch(`${config.apiServer}/api/operator/conversations/${id}/assignees`);
    if (!res.ok) await throwActionError(res, 'assignees');
    const body = (await res.json()) as { assignees?: Assignee[] };
    return body.assignees ?? [];
  },

  /**
   * Admin only (`reassign`): give the conversation to `operatorId` — from its
   * holder or from Unassigned. 409 ASSIGNMENT_CHANGED when someone took or
   * released it while the admin was choosing.
   */
  async assign(
    apiFetch: ApiFetch,
    id: string,
    operatorId: string,
    expectedOperator: string | null,
  ): Promise<{ botSilenced?: boolean } | undefined> {
    const res = await apiFetch(`${config.apiServer}/api/operator/conversations/${id}/assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ operatorId, expectedOperator }),
    });
    if (!res.ok) await throwActionError(res, 'assign');
    return res.json().catch(() => undefined);
  },

  async close(
    apiFetch: ApiFetch,
    id: string,
    reason?: ConversationClosedReason,
  ): Promise<void> {
    const res = await apiFetch(`${config.apiServer}/api/operator/conversations/${id}/close`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reason ? { reason } : {}),
    });
    if (!res.ok) await throwActionError(res, 'close');
  },

  /**
   * Unblock the sender behind this conversation.
   *
   * The counterpart to closing as Blocked/Spam. Until this existed the close
   * menu's "sender must be unblocked first" pointed at nothing, and a
   * spam-closed conversation could never be reopened.
   *
   * Uses `throwActionError` like close/release so a 4xx surfaces as a readable
   * conflict in the UI rather than a raw status code.
   */
  async unblock(apiFetch: ApiFetch, id: string): Promise<void> {
    const res = await apiFetch(`${config.apiServer}/api/operator/conversations/${id}/unblock`, {
      method: 'POST',
    });
    if (!res.ok) await throwActionError(res, 'unblock');
  },

  /**
   * chatbot v2 paused automatic replies to this customer: `resume` them now, or `allow` (mark as a real customer —
   * the loop detector skips them). v2 announces the change, which arrives as `conversation:automation:changed`.
   */
  async setAutomation(apiFetch: ApiFetch, id: string, action: 'resume' | 'allow'): Promise<void> {
    const res = await apiFetch(`${config.apiServer}/api/operator/conversations/${id}/automation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action }),
    });
    if (!res.ok) await throwActionError(res, 'automation');
  },

  async reopen(apiFetch: ApiFetch, id: string): Promise<void> {
    const res = await apiFetch(`${config.apiServer}/api/operator/conversations/${id}/reopen`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error(`reopen failed: ${res.status} ${await res.text()}`);
  },

  async markRead(apiFetch: ApiFetch, id: string): Promise<void> {
    const res = await apiFetch(`${config.apiServer}/api/operator/conversations/${id}/read`, {
      method: 'PATCH',
    });
    if (!res.ok) throw new Error(`markRead failed: ${res.status} ${await res.text()}`);
  },

  /**
   * Re-fetch the sender's profile from the Graph API, bypassing the gateway's
   * 24h freshness gate. The manual escape hatch for a conversation stuck
   * showing a raw PSID.
   *
   * Unlike the other actions this does **not** throw on an unsuccessful
   * refresh: the gateway answers 200 with `{ok:false, reason}` for the expected
   * failures, because Graph being unreachable is the premise of the feature
   * rather than an exception. Only transport/auth faults reject.
   */
  async refreshProfile(apiFetch: ApiFetch, id: string): Promise<RefreshProfileResult> {
    const res = await apiFetch(
      `${config.apiServer}/api/operator/conversations/${id}/refresh-profile`,
      { method: 'POST' },
    );
    if (!res.ok) await throwActionError(res, 'refreshProfile');
    return (await res.json()) as RefreshProfileResult;
  },
};
